import {createRoot} from 'react-dom/client';
import {BrowserRouter} from 'react-router-dom';
import {MutationCache, QueryClient, QueryClientProvider} from '@tanstack/react-query';
import App from './App';
import {errorMessage, isRetryable} from './api/client';
import {AuthProvider} from './hooks/useAuth';
import {ThemeProvider} from './hooks/useTheme';
import {toast} from './hooks/useToast';
import {draftStore} from './lib/newTellDraft';
import './styles/app.css';

// Bring back any unfinished tell (photos included) before the first screen paints.
void draftStore.hydrate();

const queryClient = new QueryClient({
    // Never try a 4xx again: a 429 retried is a loop feeding the thing that caused it.
    defaultOptions: {queries: {retry: (count, error) => count < 1 && isRetryable(error), refetchOnWindowFocus: false}},
    // Unexpected mutation failures surface as a toast; a mutation that shows its own error
    // (a form with a FieldError) sets meta: {silent: true}.
    mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => {
            if (!mutation.meta?.silent) toast.problem(errorMessage(error));
        }
    })
});

createRoot(document.getElementById('root')!).render(
    <BrowserRouter>
        <QueryClientProvider client={queryClient}>
            <ThemeProvider>
                <AuthProvider>
                    <App />
                </AuthProvider>
            </ThemeProvider>
        </QueryClientProvider>
    </BrowserRouter>
);

// The app-shell service worker (public/sw.js): installed app opens instantly, API never cached.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js').catch(() => undefined);
    });
}
